import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {
    ECAUCContractualMode,
    EnergyCommunity,
    EntitySubType,
    EntityType,
    SpatialCoordinates,
    UpdateEnergyCommunityRequest
} from 'smag-commons/cfg/hierarchy';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {I18NService} from '../../i18n/i18n.service';
import * as moment from 'moment';
import {first, map} from 'rxjs/operators';
import {HierarchyEntity, HierarchyService} from '../../shared/hierarchy.service';
import {
    ENTITY_NAME_FORBIDDEN_CHARACTERS,
    ENTITY_NAME_MAX_LENGTH,
    ENTITY_NAME_VALIDATION_PATTERN,
    entityNameError
} from '../../shared/entity-names';
import {validateNotBlank} from '../../commons/validators';
import {AddressExt} from '../../shared/google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {getZone} from '../../api/google-api.service';
import {DialogService} from '../../shared/dialog/dialog.service';
import {copyAddress} from '../ec-creator/ec-creator.component';
import {SmagApiService} from '../../api/smag-api.service';
import {addressToString} from '../../shared/energy-community';
import {ECAUCContractualModeConfig} from 'smag-commons/dto/api/customer';
import {CollectiveSelfConsumptionGroup} from 'smag-commons/cfg/hierarchy/entities';

@Component({
    selector: 'app-ec-editor',
    templateUrl: './ec-editor.component.html',
    styleUrls: ['./ec-editor.component.scss']
})
export class EcEditorComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private selectedAddress: AddressExt;
    private selectedMunicipalityCoordinates: SpatialCoordinates;
    activationDateReadonly: boolean; // true if date is already set
    communityForm: FormGroup;
    contractualMode: ECAUCContractualMode;
    contractualModes: ECAUCContractualModeConfig[] = [];
    @Input() community: EnergyCommunity;
    @Input() hierarchyEntity: HierarchyEntity;
    @Output() saved: EventEmitter<UpdateEnergyCommunityRequest> = new EventEmitter<UpdateEnergyCommunityRequest>();
    @ViewChild('selectActivationDate') private selectActivationDateDialog: TemplateRef<any>;
    @ViewChild('updatePod') private updatePodDialog: TemplateRef<any>;

    readonly ENTITY_NAME_MAX_LENGTH = ENTITY_NAME_MAX_LENGTH;
    readonly EntityType = EntityType;
    readonly EntitySubType = EntitySubType;

    constructor(private readonly i18n: I18NService,
                private readonly fb: FormBuilder,
                private readonly apiService: SmagApiService,
                private readonly dialogService: DialogService,
                private readonly hierarchyService: HierarchyService) {
        this.communityForm = this.fb.group({
            ecName: new FormControl('',
                [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)],
                [this.validateNameUniqueness.bind(this)]),
            type: new FormControl(EntityType.ENERGY_COMMUNITY),
            subType: new FormControl(null, [Validators.required]),
            activationDate: new FormControl(null, []),
            address: new FormControl(null, [Validators.required])
        });
    }

    ngOnInit(): void {
        const ad = this.community?.activation_date ? moment(this.community.activation_date) : null;
        this.activationDateReadonly = !!ad;
        this.communityForm.setValue({
            ecName: this.hierarchyEntity?.name || null,
            activationDate: ad,
            type: this.community.type,
            subType: this.community.sub_type,
            address: addressToString(this.community.address),
        });
        if (this.isAuc) {
            this.apiService.getCustomerConfig(this.hierarchyEntity.id).subscribe(cfg => this.contractualModes = cfg.auc_contractual_modes);
            this.contractualMode = (this.community as CollectiveSelfConsumptionGroup).contractual_mode;
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get nameForbiddenCharacters(): string {
        return ENTITY_NAME_FORBIDDEN_CHARACTERS.join(', ');
    }

    get nameError(): string {
        return entityNameError(this.communityForm.controls.ecName);
    }

    get addressFormControl(): FormControl {
        return this.communityForm.controls.address as FormControl;
    }

    get isAuc() {
        return this.community.sub_type === EntitySubType.COLLECTIVE_SELF_CONSUMPTION_GROUP;
    }

    get showContractualMode() {
        return this.isAuc && this.contractualModes && this.contractualModes.length && this.contractualMode;
    }

    saveAction() {
        const activationDate = this.communityForm.controls.activationDate.value;
        if (!this.activationDateReadonly && activationDate) {
            this.apiService.communityMembersStatistics(this.hierarchyEntity.id).toPromise()
                .then(result => {
                    if ((result?.counts?.PG.active + result?.counts?.PG.inactive
                        + result?.counts?.PF.active + result?.counts?.PF.inactive) > 0) {
                        this.openUpdatePodDialog();
                    } else {
                        this.internalSave(false);
                    }
                });
        } else {
            this.internalSave(false);
        }
    }

    closePodUpdateAction(setPodActivationDate: boolean, closeFn: () => void) {
        this.internalSave(setPodActivationDate);
        closeFn();
    }

    cancel() {
        this.saved.emit(null);
    }

    selectActivationDateEvent(activationDate: any, closeFn: () => void): void {
        if (activationDate) {
            this.communityForm.controls.activationDate.setValue(activationDate);
        }
        closeFn();
    }

    openActivationDateDialog() {
        this.dialogService.open<any, EnergyCommunity>({
            title: this.i18n.translate('Community activation date'),
            template: this.selectActivationDateDialog,
            context: {currentActivationDate: this.communityForm.controls.activationDate.value}
        });
    }

    resetActivationDate() {
        this.communityForm.controls.activationDate.setValue(null);
    }

    addressChange(event: AddressExt) {
        this.selectedAddress = event;
        if (this.selectedAddress.coordinates) {
            this.selectedMunicipalityCoordinates = this.selectedAddress.coordinates;
        }
    }

    private openUpdatePodDialog() {
        this.dialogService.open<any, EnergyCommunity>({
            title: this.i18n.translate('Do you want to apply the same date to existing property units and PODs too?'),
            template: this.updatePodDialog,
            context: {activationDate: this.communityForm.controls.activationDate.value}
        });
    }

    private validateNameUniqueness(ctrl: AbstractControl): Observable<ValidationErrors | null> {
        const name = ctrl.value ? ctrl.value.trim() : null;
        if (!name || name === this.hierarchyEntity.name) {
            return of(null);
        }
        const siblings = this.hierarchyEntity.parent ?
            this.hierarchyService.findEntityById(this.hierarchyEntity.parent.id).pipe(
                map(entity => entity.children || [])
            ) :
            this.hierarchyService.hierarchy.pipe(
                map(hierarchy => hierarchy.entities || [])
            );
        return siblings.pipe(
            first(),
            map(entities => !!entities.find(sibling => sibling.name === name)),
            map(found => found ? {duplicate: true} : null)
        );
    }

    private internalSave(setPodActivationDate: boolean) {
        const activationDate = this.communityForm.controls.activationDate.value;
        const req: UpdateEnergyCommunityRequest = {
            name: this.communityForm.controls.ecName.value,
            address: this.selectedAddress ? copyAddress(this.selectedAddress) : this.community.address,
            spatial_coordinates: this.selectedAddress ? this.selectedAddress.coordinates : this.community.spatial_coordinates,
            zone: this.selectedAddress ? getZone(this.selectedAddress.region) : this.community.zone,
            activation_date: activationDate ? activationDate.valueOf() : this.community.activation_date,
            municipality_coordinates: this.selectedMunicipalityCoordinates ? this.selectedMunicipalityCoordinates : null,
            set_pod_activation_date: setPodActivationDate,
            contractual_mode: this.contractualMode
        };
        this.saved.emit(req);
    }
}
